import { FormInstance, message } from 'antd';
import { useState } from 'react';
import { VendorCreatePayload } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';
import { Vendor } from 'sb/models/vendor';
import { VendorFormValues } from '../vendorsPage.types';
import { toVendorFormValues } from '../vendorsPage.utils';

type UseVendorSelectionArgs = {
  form: FormInstance;
  getVendorDetails: (vendorId: number) => Promise<VendorAdmin | undefined>;
  updateVendor: (vendorId: number, payload: Partial<Vendor>) => Promise<VendorAdmin | undefined>;
  createVendor: (payload: VendorCreatePayload) => Promise<{ id: number; name: string } | undefined>;
};

export const useVendorSelection = ({ form, getVendorDetails, updateVendor, createVendor }: UseVendorSelectionArgs) => {
  const [updating, setUpdating] = useState(false);
  const [addOrEditVendor, setAddOrEditVendor] = useState(false);
  const [vendor, setVendor] = useState<VendorAdmin>();
  const [selectedVendor, setSelectedVendor] = useState<number>();

  const clearSelectedVendorContext = () => {
    setVendor(undefined);
    setSelectedVendor(undefined);
    setAddOrEditVendor(false);
    form.resetFields();
  };

  const refreshSelectedVendorDetails = async (vendorId: number) => {
    const refreshedVendor = await getVendorDetails(vendorId);
    if (refreshedVendor) {
      setVendor(refreshedVendor);
      setSelectedVendor(refreshedVendor.id);
      form.setFieldsValue(toVendorFormValues(refreshedVendor));
    }
  };

  const handleVendorChange = async (vendorId?: number) => {
    if (!vendorId) {
      return;
    }
    setSelectedVendor(vendorId);
    const res = await getVendorDetails(vendorId);
    if (res) {
      setVendor(res);
      form.setFieldsValue(toVendorFormValues(res));
    }
  };

  const handleSubmit = async (values: VendorFormValues) => {
    const transformedValues = { ...values, fiscal_year_end: values.fiscal_year_end?.join('-') };
    const vendorPayload = transformedValues as unknown as Partial<Vendor>;
    setUpdating(true);

    if (selectedVendor) {
      const res = await updateVendor(selectedVendor, vendorPayload);
      if (res) {
        setVendor(res);
        form.setFieldsValue(toVendorFormValues(res));
        message.success('Vendor successfully updated.');
      }
    } else {
      const createPayload: VendorCreatePayload = {
        name: values.name?.trim() || '',
        ...(values.profile ? { profile: values.profile } : {}),
        ...(values.domain ? { domain: values.domain } : {}),
        ...(values.salesforce_id ? { salesforce_id: values.salesforce_id } : {}),
        ...(values.industry ? { industry: values.industry } : {}),
        ...(values.sub_industry ? { sub_industry: values.sub_industry } : {}),
        ...(values.symbol ? { symbol: values.symbol } : {}),
        ...(values.annual_revenue !== undefined ? { annual_revenue: values.annual_revenue } : {}),
        ...(transformedValues.fiscal_year_end ? { fiscal_year_end: transformedValues.fiscal_year_end } : {}),
        ...(values.is_pinpoint !== undefined ? { is_pinpoint: values.is_pinpoint } : {})
      };
      const res = await createVendor(createPayload);
      if (res) {
        const refreshedVendor = await getVendorDetails(res.id);
        if (refreshedVendor) {
          setVendor(refreshedVendor);
          form.setFieldsValue(toVendorFormValues(refreshedVendor));
        }
        setSelectedVendor(res.id);
        message.success('Vendor successfully added.');
      }
    }

    setAddOrEditVendor(false);
    setUpdating(false);
  };

  const handleEdit = () => {
    if (vendor) {
      setSelectedVendor(vendor.id);
      form.setFieldsValue(toVendorFormValues(vendor));
      setAddOrEditVendor(true);
    }
  };

  const handleCancel = () => {
    setAddOrEditVendor(false);
    if (vendor) {
      setSelectedVendor(vendor.id);
      form.setFieldsValue(toVendorFormValues(vendor));
    }
  };

  return {
    updating,
    addOrEditVendor,
    vendor,
    selectedVendor,
    setVendor,
    setSelectedVendor,
    clearSelectedVendorContext,
    refreshSelectedVendorDetails,
    handleVendorChange,
    handleSubmit,
    handleEdit,
    handleCancel
  };
};
